import { useState, useRef, useEffect, useCallback, useTransition } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@ui';
import { NavArrowIcon, ArrowLeft } from '@ui/icons';
import { t } from '@shared/i18n';
import { verifyFirstLoginOtp, resendFirstLoginOtp } from '../model/verifyFirstLoginOtp';
import { useFirstLoginStore, STORAGE_KEY_COOLDOWN_UNTIL } from '../model/firstLoginStore';

export interface FirstLoginOtpProps {
  userId: string;
  email?: string;
  onBack: () => void;
}

const OTP_LENGTH = 4;
const RESEND_COOLDOWN = 60;

const getInitialCooldown = () => {
  if (typeof window === 'undefined') return RESEND_COOLDOWN;
  try {
    const untilStr = sessionStorage.getItem(STORAGE_KEY_COOLDOWN_UNTIL);
    if (!untilStr) {
      const until = Date.now() + RESEND_COOLDOWN * 1000;
      sessionStorage.setItem(STORAGE_KEY_COOLDOWN_UNTIL, String(until));
      return RESEND_COOLDOWN;
    }
    const until = parseInt(untilStr, 10);
    if (Number.isNaN(until)) return RESEND_COOLDOWN;
    const remaining = Math.ceil((until - Date.now()) / 1000);
    return Math.max(0, remaining);
  } catch {
    return RESEND_COOLDOWN;
  }
};

export function FirstLoginOtp({ userId, email, onBack }: FirstLoginOtpProps) {
  const navigate = useNavigate();
  const [, startTransition] = useTransition();

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(getInitialCooldown);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);

  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Focus the first input on initial mount
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          try {
            sessionStorage.removeItem(STORAGE_KEY_COOLDOWN_UNTIL);
          } catch {
            /* ignore */
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  // Handle auto-resend when returning back to OTP screen on mount after previously validating OTP
  useEffect(() => {
    const storeState = useFirstLoginStore.getState();
    if (storeState.needsResendOnReturn) {
      // Immediately clear the flag from store & sessionStorage so refresh won't resend again
      storeState.setNeedsResendOnReturn(false);

      // Reset cooldown from fresh
      const cooldownUntil = Date.now() + RESEND_COOLDOWN * 1000;
      try {
        sessionStorage.setItem(STORAGE_KEY_COOLDOWN_UNTIL, String(cooldownUntil));
      } catch {
        /* ignore */
      }

      const timer = setTimeout(() => {
        void (async () => {
          setIsResending(true);
          setError(null);
          setResendCooldown(RESEND_COOLDOWN);
          try {
            const result = await resendFirstLoginOtp(userId);
            setIsResending(false);
            if (result.ok) {
              setResendSuccess(result.message ?? 'A new verification code has been sent.');
            } else {
              setError(result.message ?? 'Failed to resend verification code.');
            }
          } catch {
            setIsResending(false);
            setError(t.login.otp.errors.network);
          }
        })();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [userId]);

  const handleSubmit = useCallback(
    async (codeOverride?: string) => {
      const fullCode = typeof codeOverride === 'string' ? codeOverride : otp.join('');
      if (fullCode.length < OTP_LENGTH) {
        setError(t.login.otp.errors.required);
        return;
      }

      setError(null);
      setIsSubmitting(true);

      try {
        const result = await verifyFirstLoginOtp({
          userId,
          otp: fullCode,
        });

        if (!result.ok) {
          setError(result.message);
          setIsSubmitting(false);
          return;
        }

        // Mark needsResendOnReturn so if user navigates back to OTP screen, a new code is resent
        useFirstLoginStore.getState().setNeedsResendOnReturn(true);

        // If a token was returned, navigate to Reset Password page with token
        if (result.token) {
          const token = result.token;
          startTransition(() => {
            void navigate({
              to: '/r',
              search: { t: token },
            });
          });
        } else {
          // Fallback: If no direct token returned, check if direct reset route can be opened
          startTransition(() => {
            void navigate({
              to: '/r',
              search: { t: userId },
            });
          });
        }
      } catch {
        setError(t.login.otp.errors.network);
      } finally {
        setIsSubmitting(false);
      }
    },
    [otp, userId, navigate],
  );

  const handleOtpChange = (index: number, value: string) => {
    setError(null);
    setResendSuccess(null);

    // Only allow single digit
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned && value !== '') return;

    const char = cleaned.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = char;
    setOtp(newOtp);

    if (char && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    const fullCode = newOtp.join('');
    if (fullCode.length === OTP_LENGTH && !newOtp.includes('')) {
      void handleSubmit(fullCode);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim().replace(/\D/g, '');
    if (!pasted) return;

    const digits = pasted.slice(0, OTP_LENGTH).split('');
    const newOtp = [...otp];
    digits.forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);

    const nextIndex = Math.min(digits.length, OTP_LENGTH - 1);
    inputsRef.current[nextIndex]?.focus();

    const fullCode = newOtp.join('');
    if (fullCode.length === OTP_LENGTH && !newOtp.includes('')) {
      void handleSubmit(fullCode);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    setError(null);
    setResendSuccess(null);

    try {
      const result = await resendFirstLoginOtp(userId);
      setIsResending(false);

      if (result.ok) {
        const cooldownUntil = Date.now() + RESEND_COOLDOWN * 1000;
        try {
          sessionStorage.setItem(STORAGE_KEY_COOLDOWN_UNTIL, String(cooldownUntil));
        } catch {
          /* ignore */
        }
        setResendCooldown(RESEND_COOLDOWN);
        setResendSuccess(result.message ?? 'Verification code resent successfully.');
      } else {
        setError(result.message ?? 'Failed to resend verification code.');
      }
    } catch {
      setIsResending(false);
      setError(t.login.otp.errors.network);
    }
  };

  return (
    <div className="mt-8 flex flex-col gap-6">
      <div className="flex flex-col text-center">
        <h2 className="text-xl font-bold tracking-tight text-fg">{t.login.otp.title}</h2>
        <p className="mt-2 text-sm text-muted leading-relaxed">
          {t.login.otp.description}{' '}
          {email ? <strong className="font-semibold text-fg">{email}</strong> : null}
        </p>
      </div>

      {/* 4-digit OTP Inputs */}
      <div className="flex justify-center gap-3 sm:gap-4" onPaste={handlePaste}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{1}"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="size-13 sm:size-15 rounded-xl border border-border-soft bg-surface-raised text-center font-display text-2xl font-bold text-fg transition-all placeholder:text-fg/30 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        ))}
      </div>

      {error && (
        <p
          role="alert"
          className="-mt-1 rounded-lg bg-danger/10 px-3 py-2 text-sm font-medium text-danger-strong text-center"
        >
          {error}
        </p>
      )}

      {resendSuccess && (
        <p
          role="status"
          className="-mt-1 rounded-lg bg-success/10 px-3 py-2 text-sm font-medium text-success text-center"
        >
          {resendSuccess}
        </p>
      )}

      <Button
        type="button"
        variant="cta"
        shape="cta"
        size="lg"
        fullWidth
        disabled={isSubmitting || otp.some((d) => d === '')}
        onClick={() => void handleSubmit()}
        className="mt-2"
      >
        {isSubmitting ? (
          t.login.otp.verifying
        ) : (
          <>
            {t.login.otp.verify} <NavArrowIcon aria-hidden className="size-3.5" />
          </>
        )}
      </Button>

      {/* Resend Code */}
      <div className="flex justify-center text-sm">
        {resendCooldown > 0 ? (
          <span className="text-muted">
            {t.login.otp.resendIn}{' '}
            <strong className="font-semibold text-fg">
              {resendCooldown}
              {t.login.otp.seconds}
            </strong>
          </span>
        ) : (
          <button
            type="button"
            disabled={isResending}
            onClick={() => void handleResend()}
            className="cursor-pointer font-medium text-link hover:underline disabled:opacity-50"
          >
            {isResending ? 'Sending…' : t.login.otp.resend}
          </button>
        )}
      </div>
      <div className="flex justify-center">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted hover:text-fg transition-colors"
        >
          <ArrowLeft className="size-4" />
          <span>{t.login.otp.backToLogin}</span>
        </button>
      </div>
    </div>
  );
}
